import FileDownload from "@mui/icons-material/FileDownload";
import {
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { ExportDataButton } from "components/ExportDataButton";
import { useCurrentUser } from "hooks/useCurrentUser";
import { ExportParams, useExportData } from "hooks/useExportData";
import { useCallback, useState } from "react";

type Props = {
  tenantId: string;
  fileName: string;
  search: string;
  selectedIds: string[];
  /**
   * Staff-only. Controls whether the menu surfaces the discrepancy export and
   * its options dialog. When false we fall back to the plain manifest export
   * button so non-staff users see the same affordance as before.
   */
  showDiscrepancyCounts: boolean;
};

export function ManifestsExportMenu({
  tenantId,
  fileName,
  search,
  selectedIds,
  showDiscrepancyCounts,
}: Props) {
  const alerts = useAlerts();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showDiscrepancyDialog, setShowDiscrepancyDialog] = useState(false);
  const [includeChangeHistory, setIncludeChangeHistory] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const { isStaff: viewerIsStaff } = useCurrentUser();
  const { startExport, status } = useExportData({ autoDownload: true });
  const isExporting = status === "loading";

  const closeMenu = () => setAnchorEl(null);

  const runManifestExport = useCallback(() => {
    closeMenu();
    const params: ExportParams = {
      exportType: "wasteManifests",
      tenantId,
      fileName,
      filter: { search, viewerIsStaff },
    };
    alerts.success("Generating file...");
    startExport(params);
  }, [tenantId, fileName, search, alerts, startExport, viewerIsStaff]);

  const runDiscrepancyExport = useCallback(() => {
    setShowDiscrepancyDialog(false);
    const params: ExportParams = {
      exportType: "manifestDiscrepancies",
      tenantId,
      fileName: `${fileName}-discrepancies`,
      filter: {
        search,
        ...(selectedIds.length > 0 ? { processedManifestIds: selectedIds } : {}),
        ...(includeChangeHistory ? { includeChangeHistory: true } : {}),
      },
    };
    alerts.success("Generating discrepancy export...");
    startExport(params);
  }, [tenantId, fileName, search, selectedIds, includeChangeHistory, alerts, startExport]);

  // Non-staff / flag off: preserve the original single-purpose button so we
  // don't change the UX for anyone who can't see discrepancies anyway.
  if (!showDiscrepancyCounts) {
    return (
      <ExportDataButton
        fileName={fileName}
        tenantId={tenantId}
        filter={{ search, viewerIsStaff }}
        exportType="wasteManifests"
        iconOnly
      />
    );
  }

  const discrepancyLabel =
    selectedIds.length > 0
      ? `Export Discrepancies (${selectedIds.length})…`
      : "Export Discrepancies…";

  return (
    <>
      <Tooltip title="Export">
        <IconButton
          size="medium"
          color="info"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          loading={isExporting}
          aria-controls={menuOpen ? "manifests-export-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
        >
          <FileDownload />
        </IconButton>
      </Tooltip>
      <Menu
        id="manifests-export-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={runManifestExport}>Export Manifests</MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setShowDiscrepancyDialog(true);
          }}
        >
          {discrepancyLabel}
        </MenuItem>
      </Menu>

      <Dialog
        open={showDiscrepancyDialog}
        onClose={() => setShowDiscrepancyDialog(false)}
        title="Export Discrepancies"
        description={
          selectedIds.length > 0
            ? `Exports discrepancies for the ${selectedIds.length} selected manifest${
                selectedIds.length === 1 ? "" : "s"
              }.`
            : "Exports discrepancies for every manifest matching the current filter."
        }
        maxWidth="sm"
        fullWidth
        primaryActionButtonProps={{
          saveText: "Export",
          onClick: runDiscrepancyExport,
          hideStartIcon: true,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={includeChangeHistory}
              onChange={(e) => setIncludeChangeHistory(e.target.checked)}
            />
          }
          label="Include change history"
        />
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Adds a second sheet with one row per discrepancy event (opens, resolves, re-opens, and
          value changes). Useful for auditing resolution timelines; adds file size and generation
          time.
        </Typography>
      </Dialog>
    </>
  );
}
